<template>
  <div class="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
    <div class="max-w-3xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
      <div class="bg-blue-600 p-6 flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-white">Teacher Swap Request</h2>
          <p class="text-blue-100 mt-1">Fill in the details to submit your swap request</p>
        </div>
        <svg class="text-white w-8 h-8" fill="none" stroke="currentColor" stroke-width="2"
             viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 3L4 7L8 11"/>
          <path d="M4 7H20"/>
          <path d="M16 21L20 17L16 13"/>
          <path d="M20 17H4"/>
        </svg>
      </div>

      <form class="p-6" @submit.prevent="submitSwap">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- From Location Section -->
          <div class="bg-blue-50 rounded-xl p-4">
            <div class="flex items-center gap-2 mb-4">
              <svg class="text-blue-600 w-5 h-5" fill="none" stroke="currentColor" stroke-width="2"
                   viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <h3 class="text-lg font-semibold text-blue-800">Current Location</h3>
            </div>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">County</label>
                <select
                    v-model="fromCounty"
                    class="w-full bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm py-2"
                    @change="handleFromCountyChange"
                >
                  <option value="">Select County</option>
                  <option v-for="county in counties" :key="county.code" :value="county.code">
                    {{ county.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Constituency</label>
                <select
                    v-model="fromConstituency"
                    :disabled="!fromCounty"
                    class="w-full bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm py-2"
                    @change="handleFromConstituencyChange"
                >
                  <option value="">Select Constituency</option>
                  <option v-for="constituency in fromConstituenciesFiltered" :key="constituency.code"
                          :value="constituency.code">
                    {{ constituency.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Ward</label>
                <select
                    v-model="fromWard"
                    :disabled="!fromConstituency"
                    class="w-full bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm py-2"
                >
                  <option value="">Select Ward</option>
                  <option v-for="ward in fromWardsFiltered" :key="ward.code" :value="ward.code">
                    {{ ward.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- To Location Section -->
          <div class="bg-green-50 rounded-xl p-4">
            <div class="flex items-center gap-2 mb-4">
              <svg class="text-green-600 w-5 h-5" fill="none" stroke="currentColor" stroke-width="2"
                   viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <h3 class="text-lg font-semibold text-green-800">Desired Location</h3>
            </div>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">County</label>
                <select
                    v-model="toCounty"
                    class="w-full bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 text-sm py-2"
                    @change="handleToCountyChange"
                >
                  <option value="">Select County</option>
                  <option v-for="county in counties" :key="county.code" :value="county.code">
                    {{ county.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Constituency</label>
                <select
                    v-model="toConstituency"
                    :disabled="!toCounty"
                    class="w-full bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 text-sm py-2"
                    @change="handleToConstituencyChange"
                >
                  <option value="">Select Constituency</option>
                  <option v-for="constituency in toConstituenciesFiltered" :key="constituency.code"
                          :value="constituency.code">
                    {{ constituency.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Ward</label>
                <select
                    v-model="toWard"
                    :disabled="!toConstituency"
                    class="w-full bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 text-sm py-2"
                >
                  <option value="">Select Ward</option>
                  <option v-for="ward in toWardsFiltered" :key="ward.code" :value="ward.code">
                    {{ ward.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Teaching Details Section -->
        <div class="mt-6 bg-gray-50 rounded-xl p-4">
          <div class="flex items-center gap-2 mb-4">
            <svg class="text-gray-600 w-5 h-5" fill="none" stroke="currentColor" stroke-width="2"
                 viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
            <h3 class="text-lg font-semibold text-gray-800">Teaching Details</h3>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Education Level</label>
              <select
                  v-model="selectedLevel"
                  class="w-full bg-white rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm py-2"
              >
                <option value="">Select Level</option>
                <option v-for="level in levels" :key="level" :value="level">
                  {{ level }}
                </option>
              </select>
            </div>

            <div v-if="requiresSubjects" class="mb-4">
              <label class="block mb-2" for="subjects">Subjects</label>
              <multiselect
                  v-model="selectedSubjects"
                  :max="2"
                  :multiple="true"
                  :options="subjectOptions"
                  placeholder="Select up to 2 subjects"
              ></multiselect>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-row justify-between gap-3">
          <NuxtLink to="/swaps">
            <button
                :disabled=" isSubmitting"
                class="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                type="submit"

            > Cancel
            </button>
          </NuxtLink>
          <button
              :disabled="!isFormValid || isSubmitting"
              class="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              type="submit"
          >
            <template v-if="!isSubmitting">
              Submit Swap Request
            </template>
            <template v-else>
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"
                   xmlns="http://www.w3.org/2000/svg">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 6.627 5.373 12 12 12v-4c-4.418 0-8-3.582-8-8z"
                      fill="currentColor"></path>
              </svg>
              Submitting...
            </template>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<!--<script setup>-->
<!--import {ref, computed, onMounted} from 'vue';-->
<!--import {useRouter} from 'vue-router';-->
<!--import {useSupabaseClient, useSupabaseUser} from '#imports';-->
<!--import Multiselect from 'vue-multiselect';-->
<!--import {useToast} from "vue-toastification";-->

<!--const router = useRouter();-->
<!--const supabase = useSupabaseClient();-->
<!--const user = useSupabaseUser();-->

<!--// Dropdown data-->
<!--const counties = ref([]);-->
<!--const constituencies = ref([]);-->
<!--const wards = ref([]);-->

<!--// Selected values-->
<!--const fromCounty = ref('');-->
<!--const fromConstituency = ref('');-->
<!--const fromWard = ref('');-->
<!--const toCounty = ref('');-->
<!--const toConstituency = ref('');-->
<!--const toWard = ref('');-->
<!--const selectedLevel = ref('');-->
<!--const selectedSubjects = ref([]);-->

<!--// Computed properties-->
<!--const fromConstituenciesFiltered = computed(() => {-->
<!--  return constituencies.value.filter(c => c.county_code === fromCounty.value);-->
<!--});-->

<!--const fromWardsFiltered = computed(() => {-->
<!--  return wards.value.filter(w => w.constituency_code === fromConstituency.value);-->
<!--});-->

<!--const toConstituenciesFiltered = computed(() => {-->
<!--  return constituencies.value.filter(c =>-->
<!--      c.county_code === toCounty.value &&-->
<!--      c.county_code !== fromCounty.value-->
<!--  );-->
<!--});-->

<!--const toWardsFiltered = computed(() => {-->
<!--  return wards.value.filter(w =>-->
<!--      w.constituency_code === toConstituency.value &&-->
<!--      w.constituency_code !== fromConstituency.value-->
<!--  );-->
<!--});-->

<!--const requiresSubjects = computed(() =>-->
<!--    ['JSS', 'SSS', 'Secondary'].includes(selectedLevel.value)-->
<!--);-->

<!--const isFormValid = computed(() => {-->
<!--  const hasLocations = fromWard.value && toWard.value && fromWard.value !== toWard.value;-->
<!--  const hasLevel = selectedLevel.value !== '';-->
<!--  const hasRequiredSubjects = !requiresSubjects.value ||-->
<!--      (requiresSubjects.value && selectedSubjects.value.length === 2);-->
<!--  return hasLocations && hasLevel && hasRequiredSubjects;-->
<!--});-->

<!--// Constants-->
<!--const levels = ['Primary', 'JSS', 'SSS', 'Secondary'];-->
<!--const subjectOptions = ['Math', 'English', 'Biology', 'Chemistry', 'Physics', 'History', 'CRE', 'Physics', 'Social Studies'];-->

<!--// Location change handlers-->
<!--const handleFromCountyChange = () => {-->
<!--  fromConstituency.value = '';-->
<!--  fromWard.value = '';-->
<!--  if (fromCounty.value === toCounty.value) {-->
<!--    toCounty.value = '';-->
<!--    toConstituency.value = '';-->
<!--    toWard.value = '';-->
<!--  }-->
<!--};-->

<!--const handleFromConstituencyChange = () => {-->
<!--  fromWard.value = '';-->
<!--  if (fromConstituency.value === toConstituency.value) {-->
<!--    toConstituency.value = '';-->
<!--    toWard.value = '';-->
<!--  }-->
<!--};-->

<!--const handleToCountyChange = () => {-->
<!--  toConstituency.value = '';-->
<!--  toWard.value = '';-->
<!--  if (toCounty.value === fromCounty.value) {-->
<!--    toCounty.value = '';-->
<!--  }-->
<!--};-->

<!--const handleToConstituencyChange = () => {-->
<!--  toWard.value = '';-->
<!--  if (toConstituency.value === fromConstituency.value) {-->
<!--    toConstituency.value = '';-->
<!--  }-->
<!--};-->

<!--// Fetch location data-->
<!--const fetchLocations = async () => {-->
<!--  try {-->
<!--    const [countiesResponse, constituenciesResponse, wardsResponse] = await Promise.all([-->
<!--      supabase.from('counties').select('*').order('name'),-->
<!--      supabase.from('constituencies').select('*').order('name'),-->
<!--      supabase.from('wards').select('*').order('name'),-->
<!--    ]);-->

<!--    counties.value = countiesResponse.data || [];-->
<!--    constituencies.value = constituenciesResponse.data || [];-->
<!--    wards.value = wardsResponse.data || [];-->
<!--  } catch (error) {-->
<!--    console.error('Error fetching locations:', error);-->
<!--  }-->
<!--};-->
<!--const isSubmitting = ref(false)-->
<!--const toast = useToast()-->
<!--// Submit the swap request-->
<!--const submitSwap = async () => {-->

<!--  if (!isFormValid.value) {-->
<!--    toast.error('Please ensure all fields are filled and locations are different.');-->
<!--    return;-->
<!--  }-->
<!--  isSubmitting.value = true;-->
<!--  const sortedSubjects = [...selectedSubjects.value].sort();-->

<!--  // Check if swap already exists-->
<!--  const {data: existingSwap, error: checkError} = await supabase-->
<!--      .from('swaps')-->
<!--      .select('*')-->
<!--      .eq('from_ward_code', fromWard.value)-->
<!--      .eq('to_ward_code', toWard.value)-->
<!--      .containedBy('subjects', sortedSubjects)-->
<!--      .single();-->

<!--  if (existingSwap) {-->
<!--    toast.warning('A similar swap request already exists.');-->
<!--    return;-->
<!--  }-->

<!--  if (checkError && checkError.code !== 'PGRST116') {-->
<!--    console.error('Error checking existing swap:', checkError);-->
<!--    toast.error('An error occurred. Please try again.');-->
<!--    return;-->
<!--  }-->

<!--  const {error} = await supabase.from('swaps').insert({-->
<!--    user_id: user.value.id,-->
<!--    from_ward_code: fromWard.value,-->
<!--    to_ward_code: toWard.value,-->
<!--    level: selectedLevel.value,-->
<!--    subjects: sortedSubjects,-->
<!--    is_active: true,-->
<!--  });-->

<!--  if (error) {-->
<!--    console.error('Error inserting swap:', error);-->
<!--    toast.error('Failed to submit swap. Please try again.');-->
<!--  } else {-->
<!--    toast.success('Swap submitted successfully!');-->
<!--    await router.push('/swaps');-->
<!--  }-->
<!--  isSubmitting.value = false;-->
<!--};-->

<!--onMounted(() => {-->
<!--  fetchLocations();-->
<!--});-->
<!--</script>-->
<script setup>
import {ref, computed, onMounted} from 'vue';
import {useRouter} from 'vue-router';
import {useSupabaseClient, useSupabaseUser} from '#imports';
import Multiselect from 'vue-multiselect';
import {useToast} from "vue-toastification";

const router = useRouter();
const supabase = useSupabaseClient();
const user = useSupabaseUser();

// Dropdown data
const counties = ref([]);
const constituencies = ref([]);
const wards = ref([]);

// Selected values
const fromCounty = ref('');
const fromConstituency = ref('');
const fromWard = ref('');
const toCounty = ref('');
const toConstituency = ref('');
const toWard = ref('');
const selectedLevel = ref('');
const selectedSubjects = ref([]);

// Computed properties
const fromConstituenciesFiltered = computed(() => {
  return constituencies.value.filter(c => c.county_code === fromCounty.value);
});

const fromWardsFiltered = computed(() => {
  return wards.value.filter(w => w.constituency_code === fromConstituency.value);
});

// Modified to allow same county and constituency
const toConstituenciesFiltered = computed(() => {
  return constituencies.value.filter(c => c.county_code === toCounty.value);
});

// Modified to allow same constituency but prevent same ward
const toWardsFiltered = computed(() => {
  return wards.value.filter(w =>
      w.constituency_code === toConstituency.value &&
      w.code !== fromWard.value
  );
});

const requiresSubjects = computed(() =>
    ['JSS', 'SSS', 'Secondary'].includes(selectedLevel.value)
);

const isFormValid = computed(() => {
  const hasLocations = fromWard.value && toWard.value && fromWard.value !== toWard.value;
  const hasLevel = selectedLevel.value !== '';
  const hasRequiredSubjects = !requiresSubjects.value ||
      (requiresSubjects.value && selectedSubjects.value.length === 2);
  return hasLocations && hasLevel && hasRequiredSubjects;
});

// Constants
const levels = ['Primary', 'JSS', 'SSS', 'Secondary'];
const subjectOptions = [
  'Mathematics',
  'English',
  'Kiswahili',
  'Biology',
  'Chemistry',
  'Physics',
  'History',
  'Geography',
  'CRE',
  'IRE',
  'HRE',
  'Social Studies',
  'Business Studies',
  'Agriculture',
  'Home Science',
  'ICT',
  'Computer Studies',
  'Music',
  'Art and Design',
  'Performing Arts',
  'Visual Arts',
  'Sports Science',
  'Technical Education',
  'Physical and Health Education',
  'Integrated Science',
];

// Modified location change handlers to only reset dependent fields
const handleFromCountyChange = () => {
  fromConstituency.value = '';
  fromWard.value = '';
};

const handleFromConstituencyChange = () => {
  fromWard.value = '';
};

const handleToCountyChange = () => {
  toConstituency.value = '';
  toWard.value = '';
};

const handleToConstituencyChange = () => {
  toWard.value = '';
};

// Fetch location data
const fetchLocations = async () => {
  try {
    const [countiesResponse, constituenciesResponse, wardsResponse] = await Promise.all([
      supabase.from('counties').select('*').order('name'),
      supabase.from('constituencies').select('*').order('name'),
      supabase.from('wards').select('*').order('name'),
    ]);

    counties.value = countiesResponse.data || [];
    constituencies.value = constituenciesResponse.data || [];
    wards.value = wardsResponse.data || [];
  } catch (error) {
    console.error('Error fetching locations:', error);
  }
};

const isSubmitting = ref(false);
const toast = useToast();

// Submit the swap request
const submitSwap = async () => {
  if (!isFormValid.value) {
    toast.error('Please ensure all fields are filled and wards are different.');
    return;
  }

  isSubmitting.value = true;
  const sortedSubjects = [...selectedSubjects.value].sort();

  // Check if swap already exists
  const {data: existingSwap, error: checkError} = await supabase
      .from('swaps')
      .select('*')
      .eq('from_ward_code', fromWard.value)
      .eq('to_ward_code', toWard.value)
      .containedBy('subjects', sortedSubjects)
      .single();

  if (existingSwap) {
    toast.warning('A similar swap request already exists.');
    isSubmitting.value = false;
    return;
  }

  if (checkError && checkError.code !== 'PGRST116') {
    console.error('Error checking existing swap:', checkError);
    toast.error('An error occurred. Please try again.');
    isSubmitting.value = false;
    return;
  }

  const {error} = await supabase.from('swaps').insert({
    user_id: user.value.id,
    from_ward_code: fromWard.value,
    to_ward_code: toWard.value,
    level: selectedLevel.value,
    subjects: sortedSubjects,
    is_active: true,
  });

  if (error) {
    console.error('Error inserting swap:', error);
    toast.error('Failed to submit swap. Please try again.');
  } else {
    toast.success('Swap submitted successfully!');
    await router.push('/swaps');
  }
  isSubmitting.value = false;
};

onMounted(() => {
  fetchLocations();
});

definePageMeta({
  middleware: 'auth'
})
</script>
<style>
@import "vue-multiselect/dist/vue-multiselect.min.css";
</style>